import { hooks } from '@feathersjs/hooks'
import type { BlobObject } from '@nuxthub/core'
import * as dbHooks from './hooks/db'

export type TData = { id?: string, [k: string]: unknown }

export const useApiDB = <T extends TData>(tableName: TableName) => {
  const $fetch = useRequestFetch() // IMPORTANT: send cookies along on SSR (pending merge https://github.com/nuxt/nuxt/issues/24813)

  const onSubmitCreate = async (row: Partial<T>, toast = true) => {
    const result = await $fetch<T>(`/api/db/${tableName}`, {
      method: 'POST',
      body: row,
    })
    return result
  }

  const onSubmitUpdate = async (row: Partial<T>, toast = true) => {
    const result = await $fetch<T>(`/api/db/${tableName}/${row.id}`, {
      method: 'PATCH',
      body: row,
    })
    return result
  }

  const onSubmitDelete = async (rows: (Partial<T> & { id: T['id'] })[], toast = true) => {
    const result = await Promise.all(rows.map((row) => {
      const url = `/api/db/${tableName}/${row.id}`
      return $fetch(url, { method: 'DELETE' })
    }))

    // [blob] remove eventual associated files from blob:
    await Promise.all(rows.map((row) => {
      const { removeFile } = useApiBlob(tableName, row.id)
      Object.keys(row).forEach((key) => {
        if (key.startsWith('image')) {
          return removeFile(row[key] as BlobObject)
        }
        else if (key.startsWith('file')) {
          return removeFile(row[key] as BlobObject)
        }
        else if (['files'].includes(key)) {
          return Promise.all((row[key] as BlobObject[]).map(removeFile))
        }
      })
    }))

    return result
  }

  const onSubmitUpdateMultiple = async (rows: Partial<T>[], toast = true) => {
    const result = await Promise.all(
      rows.map((row) => {
        const url = `/api/db/${tableName}/${row.id}`
        return $fetch(url, { method: 'PATCH', body: row })
      }),
    )
    return result
  }

  return {
    onSubmitCreate: hooks(onSubmitCreate, [
      dbHooks.handleErrors('Registro creado con éxito', 'Error al crear registro'),
      dbHooks.handleCreate(tableName),
    ]),
    onSubmitUpdate: hooks(onSubmitUpdate, [
      dbHooks.handleErrors('Registro actualizado con éxito', 'Error al actualizar registro'),
      dbHooks.handleCascadeUpdates(tableName),
      dbHooks.handleUpdate(tableName),
    ]),
    onSubmitDelete: hooks(onSubmitDelete, [
      dbHooks.handleErrors('Registro(s) eliminado(s) con éxito', 'Error al eliminar registro(s)'),
      dbHooks.handleDelete(tableName),
    ]),
    onSubmitUpdateMultiple: hooks(onSubmitUpdateMultiple, [
      dbHooks.handleErrors('Registros actualizados con éxito', 'Error al actualizar registros'),
      dbHooks.handleCascadeUpdatesMultiple(tableName),
    ]),
  }
}
